import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger 
} from "../components/ui/dialog";
import Navbar from '../components/ui/Navbar';

import { 
  Briefcase, User, CheckCircle2, MapPin, Star, ShieldCheck, 
  Zap, ArrowLeft, MessageSquare, Loader2, Phone, Award, Calendar, Quote, ListFilter, Info, Ban
} from 'lucide-react';
import { toast } from 'sonner';

import planoFundo from "../assets/planodefundo.png"; 

const ProfileView = () => {
  const { techId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tech, setTech] = useState(null);
  const [reviews, setReviews] = useState([]);
  
  // Estados de Filtro e Ordenação
  const [starFilter, setStarRating] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // Novo: Finalizada ou Cancelada
  const [sortBy, setSortBy] = useState('newest');

  const formatPhone = (v) => {
    if (!v) return "Não informado";
    const n = v.replace(/\D/g, "");
    if (n.length <= 10) return n.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    return n.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  };

  useEffect(() => {
    const fetchTechData = async () => {
      try {
        const techDoc = await getDoc(doc(db, "users", techId));
        if (techDoc.exists()) {
          setTech({ id: techDoc.id, ...techDoc.data() });
        } else {
          toast.error("Técnico não encontrado.");
          navigate('/dashboard');
          return;
        }

        const q = query(collection(db, 'orders'), where('technicianId', '==', techId));
        const querySnapshot = await getDocs(q);
        const reviewsData = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(order => order.rating && order.rating > 0);
        
        setReviews(reviewsData);
      } catch (e) {
        console.error(e);
        toast.error("Erro ao carregar perfil.");
      } finally {
        setLoading(false);
      }
    };
    if (techId) fetchTechData();
  }, [techId, navigate]);

  const filteredAndSortedReviews = useMemo(() => {
    let result = [...reviews];
    
    // Filtro por Estrela
    if (starFilter !== 'all') result = result.filter(r => r.rating === parseInt(starFilter));
    
    // Filtro por Status da OS
    if (statusFilter !== 'all') result = result.filter(r => r.status === statusFilter);
    
    // Ordenação
    if (sortBy === 'best') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'oldest') {
      result.sort((a, b) => new Date(a.ratedAt || a.updatedAt || 0) - new Date(b.ratedAt || b.updatedAt || 0));
    } else {
      result.sort((a, b) => new Date(b.ratedAt || b.updatedAt || 0) - new Date(a.ratedAt || a.updatedAt || 0));
    }
    return result;
  }, [reviews, starFilter, statusFilter, sortBy]);

  const averageRating = reviews.length > 0 
    ? reviews.reduce((acc, curr) => acc + curr.rating, 0) / reviews.length
    : 0;

  if (loading) return <div className="h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-blue-600" /></div>;
  if (!tech) return null;

  const pData = tech.professionalData || {};
  const areas = pData.areas || tech.areas || [];

  return (
    <div className="relative min-h-screen font-sans text-slate-900 text-left bg-slate-50/30">
      <div className="fixed inset-0 -z-10 opacity-10" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
      <Navbar />

      <main className="max-w-6xl mx-auto px-6 pt-32 pb-20">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[10px] font-black uppercase italic text-slate-400 hover:text-blue-600 mb-8 transition-all">
          <ArrowLeft size={16} /> Voltar para a busca
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* CARD ESQUERDA */}
          <div className="lg:col-span-4 space-y-6">
            <Card className="rounded-[2.5rem] border-none shadow-2xl bg-white p-8 text-center flex flex-col items-center">
               <div className="relative mb-6">
                  <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-orange-500 p-1 bg-white shadow-xl flex items-center justify-center">
                    {pData.photoUrl || tech.photoURL ? (
                      <img src={pData.photoUrl || tech.photoURL} className="w-full h-full object-cover rounded-full" alt="Foto" />
                    ) : ( <User className="w-12 h-12 text-slate-200" /> )}
                  </div>
                  <div className="absolute bottom-1 right-1 bg-blue-600 text-white p-2.5 rounded-2xl shadow-lg border-2 border-white"><ShieldCheck size={20} /></div>
               </div>
               
               <h2 className="text-2xl font-black uppercase italic tracking-tighter mb-1 leading-tight">{pData.name || tech.name}</h2>
               <Badge className="bg-blue-50 text-blue-600 border-none font-black uppercase italic text-[8px] mb-6 px-3 py-1">Técnico Verificado</Badge>

               <div className="flex items-center justify-center gap-1.5 bg-slate-50 px-5 py-2.5 rounded-2xl border border-slate-100 mb-8 shadow-inner">
                  <Star size={16} className="fill-yellow-400 text-yellow-400" />
                  <span className="text-lg font-black text-slate-900 italic">{averageRating > 0 ? averageRating.toFixed(1) : 'Novo'}</span>
               </div>

               <div className="w-full pt-6 border-t border-slate-50 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase text-slate-400 italic">Cidade</span>
                    <span className="text-[11px] font-black uppercase italic text-slate-700 flex items-center gap-1">
                      <MapPin size={12} className="text-red-500" /> {pData.address?.city || tech.addressDetails?.city || "---"}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between bg-blue-50/50 p-4 rounded-2xl border border-blue-100">
                    <div className="text-left text-left">
                        <span className="text-[9px] font-black text-blue-600 uppercase italic block mb-0.5">Contato</span>
                        <span className="text-sm font-black text-blue-900">
                            {formatPhone(pData.phone || tech.phone)}
                        </span>
                    </div>
                    <div className="bg-blue-600 p-2 rounded-xl text-white shadow-lg"><Phone size={16} /></div>
                  </div>
               </div>

               <Button onClick={() => navigate(`/direct-order/${techId}`)} className="w-full mt-8 h-16 bg-orange-600 hover:bg-orange-700 text-white font-black rounded-2xl shadow-xl shadow-orange-200 uppercase italic text-sm gap-3 transition-all active:scale-95">
                <Zap size={20} className="fill-white" /> SOLICITAR ORÇAMENTO
               </Button>
            </Card>
          </div>

          {/* COLUNA DIREITA */}
          <div className="lg:col-span-8 space-y-8">
            <Card className="rounded-[2.5rem] shadow-2xl bg-white/95 p-8 md:p-12 border-none">
              <CardContent className="p-0 space-y-12">
                
                {/* BIOGRAFIA PREMIUM */}
                <div className="space-y-6">
                  <h3 className="text-xs font-black uppercase text-blue-600 italic flex items-center gap-2 text-left tracking-[0.2em]">
                    <User size={18} /> Biografia Profissional
                  </h3>
                  <div className="relative">
                    <Quote className="absolute -top-4 -left-4 w-12 h-12 text-slate-100 -z-10" />
                    <p className="text-slate-700 font-bold italic leading-relaxed text-lg bg-slate-50/50 p-8 rounded-[2.5rem] border border-slate-100 shadow-inner text-left">
                        {pData.bio || "Este especialista é verificado pelo sistema Socorre Aí, garantindo um atendimento seguro e de alta qualidade."}
                    </p>
                  </div>
                </div>

                {/* ESPECIALIDADES */}
                <div className="space-y-6">
                  <h3 className="text-xs font-black uppercase text-orange-600 italic flex items-center gap-2 text-left tracking-[0.2em]">
                    <Zap size={18} className="fill-orange-500" /> Especialidades
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {areas.length > 0 ? areas.map((area, idx) => (
                      <Badge key={idx} className="bg-white border-2 border-slate-100 text-slate-700 font-black uppercase italic text-[11px] px-6 py-3 rounded-2xl shadow-sm hover:border-blue-500 transition-colors">
                        <CheckCircle2 size={14} className="text-green-500" /> {area}
                      </Badge>
                    )) : <span className="text-slate-400 italic text-sm">Especialista Geral</span>}
                  </div>
                </div>

                {/* SEÇÃO DE AVALIAÇÕES */}
                <div className="space-y-8 pt-12 border-t border-slate-100">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xs font-black uppercase text-slate-800 italic flex items-center gap-2 text-left tracking-[0.2em]">
                        <Star size={18} className="text-yellow-400" /> Reputação ({reviews.length})
                    </h3>
                    
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="ghost" className="text-[10px] font-black uppercase text-blue-600 hover:underline italic gap-2">
                            <ListFilter size={14}/> Filtrar Histórico
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl rounded-[3rem] p-0 border-none bg-white overflow-hidden shadow-2xl z-[500]">
                        <DialogHeader className="p-8 bg-slate-900 text-white text-left text-left">
                          <DialogTitle className="text-2xl font-black uppercase italic flex items-center gap-3"><Star className="text-yellow-400"/> Histórico de Atendimentos</DialogTitle>
                        </DialogHeader>
                        
                        <div className="p-6 bg-slate-100 border-b border-slate-200 flex flex-wrap gap-4 text-left">
                           <div className="flex-1 min-w-[120px]">
                              <Label className="text-[9px] font-black uppercase text-slate-400 ml-2 mb-1 block">Nota</Label>
                              <Select value={starFilter} onValueChange={setStarRating}>
                                <SelectTrigger className="rounded-xl border-slate-200 font-bold uppercase italic text-[10px] h-11 bg-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-[800]">
                                  <SelectItem value="all">Todas as Notas</SelectItem>
                                  {[5,4,3,2,1].map(s => <SelectItem key={s} value={s.toString()}>{s} Estrelas</SelectItem>)}
                                </SelectContent>
                              </Select>
                           </div>
                           <div className="flex-1 min-w-[120px]">
                              <Label className="text-[9px] font-black uppercase text-slate-400 ml-2 mb-1 block">Status OS</Label>
                              <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="rounded-xl border-slate-200 font-bold uppercase italic text-[10px] h-11 bg-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-[800]">
                                  <SelectItem value="all">Todos os tipos</SelectItem>
                                  <SelectItem value="completed">Somente Finalizadas</SelectItem>
                                  <SelectItem value="cancelled">Somente Canceladas</SelectItem>
                                </SelectContent>
                              </Select>
                           </div>
                           <div className="flex-1 min-w-[120px]">
                              <Label className="text-[9px] font-black uppercase text-slate-400 ml-2 mb-1 block">Ordem</Label>
                              <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger className="rounded-xl border-slate-200 font-bold uppercase italic text-[10px] h-11 bg-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-[800]">
                                  <SelectItem value="newest">Mais recentes</SelectItem>
                                  <SelectItem value="oldest">Mais antigas</SelectItem>
                                  <SelectItem value="best">Melhores notas</SelectItem>
                                </SelectContent>
                              </Select>
                           </div>
                        </div>

                        <div className="max-h-[60vh] overflow-y-auto p-8 space-y-6 custom-scrollbar text-left bg-slate-50/40">
                           {filteredAndSortedReviews.length > 0 ? filteredAndSortedReviews.map((rev, i) => {
                             const isCancelled = rev.status === 'cancelled';
                             return (
                               <div key={i} className={`p-8 rounded-[2rem] border shadow-sm transition-all bg-white ${isCancelled ? 'border-red-100' : 'border-slate-100'}`}>
                                 <div className="flex justify-between items-start mb-4">
                                    <div className="flex flex-col gap-2">
                                       <div className="flex gap-0.5">
                                         {[...Array(5)].map((_, j) => <Star key={j} size={14} className={`${j < rev.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-100'}`} />)}
                                       </div>
                                       {isCancelled && (
                                         <Badge className="bg-red-50 text-red-600 border-red-100 text-[7px] font-black uppercase italic w-fit flex items-center gap-1 shadow-none">
                                            <Ban size={8}/> OS Cancelada
                                         </Badge>
                                       )}
                                    </div>
                                    <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-500 font-black text-[9px] px-4 py-1.5 rounded-xl flex items-center gap-2 shadow-sm uppercase">
                                      <Calendar size={10} /> {new Date(rev.ratedAt || rev.updatedAt).toLocaleDateString()}
                                    </Badge>
                                 </div>
                                 <p className="text-slate-700 italic font-bold text-base leading-relaxed pr-2">"{rev.ratingComment || 'Excelente profissional.'}"</p>
                               </div>
                             );
                           }) : (
                             <div className="text-center py-20 text-slate-300 font-black uppercase italic">Nenhuma avaliação encontrada com estes filtros.</div>
                           )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  
                  {/* PREVIEW ÚLTIMAS 3 */}
                  <div className="space-y-4">
                    {reviews.length > 0 ? reviews.slice(0, 3).map((rev, idx) => (
                      <div key={idx} className={`p-6 rounded-[2rem] border text-left bg-slate-50/50 ${rev.status === 'cancelled' ? 'border-red-100' : 'border-slate-100'}`}>
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center gap-3">
                             <div className="flex gap-0.5">
                                {[...Array(5)].map((_, i) => <Star key={i} size={12} className={`${i < rev.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} />)}
                             </div>
                             {rev.status === 'cancelled' && <Badge className="bg-red-600 text-white text-[6px] uppercase font-black px-2 py-0.5 border-none">CANCELADA</Badge>}
                          </div>
                          <Badge variant="secondary" className="bg-white text-slate-400 border-none text-[8px] font-black italic px-2 py-0.5 rounded-md">
                             {new Date(rev.ratedAt || rev.updatedAt).toLocaleDateString()}
                          </Badge>
                        </div>
                        <p className="text-slate-600 italic text-sm font-bold">"{rev.ratingComment || 'Avaliou positivamente.'}"</p>
                      </div>
                    )) : (
                      <div className="text-center py-10 opacity-30 italic text-xs font-black uppercase">Sem avaliações detalhadas ainda.</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <style dangerouslySetInnerHTML={{ __html: `.custom-scrollbar::-webkit-scrollbar { width: 5px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }` }} />
    </div>
  );
};

export default ProfileView;